import { normalize, schema, denormalize } from 'normalizr';
import { RecordingSource } from '../../Utils/Constants';
import { isInRange } from '../../Utils/MathUtils';

/**
 * This method normalizes object containing list of recordings
 * It returns object containing 2 keys
 * 1st is byKey. This is an object of recordingKey and recording
 * 2nd is allKeys. This is an array containing all recordingKeys
 *
 * @param entitiesMap
 * @returns {{byKey, allKeys}}
 */

const recording = new schema.Entity('recordings', {}, { idAttribute: 'recordingKey' });
const recordingsSchema = { recordings: [recording] };

export function normalizeRecordings(recordingsList = []) {
  if (recordingsList.length === 0) {
    return {
      byKey: {},
      allKeys: []
    };
  }
  const { result, entities } = normalize({ recordings: recordingsList }, recordingsSchema);
  return {
    byKey: entities.recordings,
    allKeys: result.recordings
  };
}

/**
 * This method de normalizes recordingsMap for given recordingKeys
 *
 * @param recordingKeys
 * @param recordingsMap
 * @returns [recordings]
 */
export function denormalizeRecordings(recordingKeys, recordingsMap) {
  if (recordingKeys === null || recordingKeys.length === 0) {
    return [];
  }
  const deNormalizedData = denormalize({ recordings: recordingKeys }, recordingsSchema, { recordings: recordingsMap });
  return deNormalizedData.recordings;
}

export function computeRecordingStats(byKey, recordingsKeys = []) {
  return recordingsKeys.reduce(
    (accumulator, currentValue) => {
      const recording = byKey[currentValue] || {};
      const isG2SRecording = [RecordingSource.G2S, RecordingSource.G2S_EMBEDDED].includes(recording.source);
      const { g2sViews, standaloneViews, g2sShares, totalVideos, totalG2SVideos, totalStandaloneVideos } = accumulator;
      return {
        totalVideos: totalVideos + 1,
        totalG2SVideos: totalG2SVideos + (isG2SRecording ? 1 : 0),
        totalStandaloneVideos: totalStandaloneVideos + (!isG2SRecording ? 1 : 0),
        g2sViews: g2sViews + (isG2SRecording ? recording.viewCount : 0),
        standaloneViews: standaloneViews + (!isG2SRecording ? recording.viewCount : 0),
        g2sShares: g2sShares + (isG2SRecording ? recording.shareCount : 0)
      };
    },
    {
      totalVideos: 0,
      totalG2SVideos: 0,
      totalStandaloneVideos: 0,
      g2sViews: 0,
      standaloneViews: 0,
      g2sShares: 0
    }
  );
}
export const getOperatorFunction = isSelected => (isSelected ? (a, b) => a + b : (a, b) => a - b);

export function getUpdatedStatsOnToggleRecordingKey(existingStats, isSelected, recording = {}) {
  let {
    filteredSelectedTotalVideos = 0,
    filteredSelectedG2STotalVideos = 0,
    filteredSelectedStandaloneTotalVideos = 0,
    filteredSelectedG2STotalViews = 0,
    filteredSelectedStandaloneTotalViews = 0,
    filteredSelectedG2STotalShares = 0
  } = existingStats;
  const operator = getOperatorFunction(isSelected);
  const { viewCount = 0, shareCount = 0, source } = recording;
  const isG2S = [RecordingSource.G2S, RecordingSource.G2S_EMBEDDED].includes(source);
  return {
    filteredSelectedTotalVideos: operator(filteredSelectedTotalVideos, 1),
    filteredSelectedG2STotalVideos: operator(filteredSelectedG2STotalVideos, isG2S ? 1 : 0),
    filteredSelectedStandaloneTotalVideos: operator(filteredSelectedStandaloneTotalVideos, !isG2S ? 1 : 0),
    filteredSelectedG2STotalViews: operator(filteredSelectedG2STotalViews, isG2S ? viewCount : 0),
    filteredSelectedStandaloneTotalViews: operator(filteredSelectedStandaloneTotalViews, !isG2S ? viewCount : 0),
    filteredSelectedG2STotalShares: operator(filteredSelectedG2STotalShares, isG2S ? shareCount : 0)
  };
}

export function getUpdatedStatsOnToggleAllRecordingKeys(existingStats, isSelected) {
  let result = {
    filteredSelectedTotalVideos: 0,
    filteredSelectedG2STotalVideos: 0,
    filteredSelectedStandaloneTotalVideos: 0,
    filteredSelectedG2STotalViews: 0,
    filteredSelectedStandaloneTotalViews: 0,
    filteredSelectedG2STotalShares: 0
  };

  if (isSelected) {
    result.filteredSelectedTotalVideos = existingStats.filteredTotalVideos;
    result.filteredSelectedG2STotalVideos = existingStats.filteredG2STotalVideos;
    result.filteredSelectedStandaloneTotalVideos = existingStats.filteredStandaloneTotalVideos;
    result.filteredSelectedG2STotalViews = existingStats.filteredG2STotalViews;
    result.filteredSelectedStandaloneTotalViews = existingStats.filteredStandaloneTotalViews;
    result.filteredSelectedG2STotalShares = existingStats.filteredG2STotalShares;
  }
  return result;
}

export function getFilteredRecordingKeys(
  recordingsMap,
  viewsRange,
  sharesRange,
  searchString,
  sources,
  allRecordingKeys = []
) {
  return allRecordingKeys.filter(recordingKey => {
    const recording = recordingsMap[recordingKey] || {};
    const { viewCount, title, shareCount, source } = recording;
    return (
      isInRange(viewCount, viewsRange) &&
      isInRange(shareCount, sharesRange) &&
      sources.includes(source) &&
      (searchString && title ? title.toUpperCase().includes(searchString.toUpperCase()) : true)
    );
  });
}

export function getCurrentRecording(recording) {
  if (recording.uniqueTableId !== 'recordingsTableInReports') {
    return { currentSession: recording.recordings, recordingsTableInReportsFlag: false };
  } else {
    return { currentSession: recording.filterRecordings, recordingsTableInReportsFlag: true };
  }
}



// WEBPACK FOOTER //
// ./src/Reducks/Recordings/RecordingsUtils.js